import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert, RegionCargando, Skeleton } from "@/components/ui";
import { useResolvedTheme } from "@/lib/theme-store";
import { CHROME, type ColorScheme } from "../utils/code-viewer-theme";
import { renderJsonEnLineas, prettyJson } from "../utils/json-highlighter";
import { renderXmlEnLineas, prettyXml } from "../utils/xml-highlighter";

/**
 * Viewer de archivos de código (XML · JSON · texto plano) con números
 * de línea y syntax highlighting. Lee el `Blob` que recibe, lo
 * convierte a texto, opcionalmente lo formatea (pretty-print) y lo
 * renderiza en una tabla 2-columnas (línea + contenido).
 *
 * El idioma se decide combinando extensión y content-type · el caller
 * no necesita preprocesar nada.
 */
export function CodeViewer({ blob, ext, contentType }: { blob: Blob; ext: string; contentType: string }) {
  const [texto, setTexto] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const scheme = useResolvedTheme() as ColorScheme;

  const language = useMemo<"xml" | "json" | "plain">(() => {
    if (ext === "xml" || contentType.includes("xml")) return "xml";
    if (ext === "json" || contentType.includes("json")) return "json";
    return "plain";
  }, [ext, contentType]);

  useEffect(() => {
    let cancelado = false;
    blob
      .text()
      .then((t) => {
        if (cancelado) return;
        if (language === "xml") setTexto(prettyXml(t));
        else if (language === "json") setTexto(prettyJson(t));
        else setTexto(t);
      })
      .catch((e: unknown) => {
        if (!cancelado) setErr(e instanceof Error ? e.message : "No se pudo leer el archivo");
      });
    return () => {
      cancelado = true;
    };
  }, [blob, language]);

  if (err) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Alert variant="error" icon={<IconAlertTriangle size={16} />}>
          {err}
        </Alert>
      </div>
    );
  }
  if (texto === null) return <CodigoSkeleton />;

  // Normaliza saltos de línea y tokeniza · necesario para que tags
  // multilínea (CFDIs suelen partir el comprobante en varias líneas)
  // mantengan highlight consistente.
  const normalizado = texto.replace(/\r\n/g, "\n");
  const lineas: ReactNode[][] =
    language === "xml" ? renderXmlEnLineas(normalizado, scheme) : language === "json" ? renderJsonEnLineas(normalizado, scheme) : normalizado.split("\n").map((l) => [l] as ReactNode[]);

  return <CodeTable lineas={lineas} scheme={scheme} />;
}

/** Líneas de código en carga · columna de números + líneas con sangría variable (forma de XML/JSON). */
const SANGRIAS = [0, 1, 2, 2, 3, 3, 2, 3, 3, 3, 2, 1, 2, 2, 1, 0];
const ANCHOS = ["55%", "70%", "48%", "62%", "40%", "58%", "66%", "35%", "52%", "60%", "45%", "68%", "50%", "38%", "56%", "30%"];

function CodigoSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando archivo…" className="size-full overflow-hidden py-1">
      {SANGRIAS.map((nivel, i) => (
        <div key={i} className="flex items-center gap-4 py-[5px]" aria-hidden="true">
          <Skeleton height={9} width={16} className="ml-3.5 shrink-0 opacity-60" />
          <Skeleton height={9} width={ANCHOS[i]} style={{ marginLeft: nivel * 16 }} />
        </div>
      ))}
    </RegionCargando>
  );
}

function CodeTable({ lineas, scheme }: { lineas: ReactNode[][]; scheme: ColorScheme }) {
  const chrome = CHROME[scheme];
  return (
    <div className="size-full overflow-auto font-mono text-[12px] leading-[1.6]" style={{ background: chrome.bg, color: chrome.fg }}>
      <table className="w-full border-collapse">
        <tbody>
          {lineas.map((nodos, idx) => (
            <tr key={idx}>
              <td
                className="whitespace-nowrap py-0 pl-3.5 pr-3 text-right align-top select-none tabular-nums"
                style={{ color: chrome.lineno, borderRight: `1px solid ${chrome.border}`, width: 1 }}
              >
                {idx + 1}
              </td>
              <td className="whitespace-pre-wrap break-words px-4 py-0 align-top">{nodos.length > 0 ? nodos : " "}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
